import { useState, type ReactNode } from 'react';
import { deviceToday, eventName, eventPlace, myShowsToday } from '@wr/shared';
import { Dialog } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import type { LocalRecord } from '../../offline/store.js';

/** "No event" chosen today on this device: capture asks no more today. */
export const DECLINED = 'eventDeclined';

interface Asking {
  shows: LocalRecord[];
  resolve(eventId: string | null | undefined): void;
}

/**
 * The event of a capture (decision of Nov 3, 2026): sellers captured at
 * Ecomondo without having picked it. With no current event while a show of
 * the person's team is on (between its dates), capture asks which before
 * saving: a show (it becomes the current event of this device) or No event
 * (not asked again today). Without such a show, nothing is asked. `undefined`: the person
 * closed the question, nothing is saved.
 */
export function useAskEvent(): {
  eventForCapture(): Promise<string | null | undefined>;
  dialog: ReactNode;
} {
  const { store, currentEventId, setEventId } = useOffline();
  const [asking, setAsking] = useState<Asking | null>(null);

  async function eventForCapture(): Promise<string | null | undefined> {
    const current = await currentEventId();
    if (current) return current;
    if (!store) return null;
    try {
      const today = deviceToday();
      if ((await store.getMeta<string>(DECLINED)) === today) return null;
      const shows = myShowsToday(await store.listRecords('event'), today);
      if (shows.length === 0) return null;
      return await new Promise<string | null | undefined>((resolve) =>
        setAsking({ shows, resolve }),
      );
    } catch {
      // The device storage failed: the capture is never held back by the question.
      return null;
    }
  }

  async function answer(eventId: string | null | undefined) {
    const current = asking;
    setAsking(null);
    if (!current) return;
    try {
      if (eventId) await setEventId(eventId);
      else if (eventId === null) await store?.setMeta(DECLINED, deviceToday());
    } finally {
      current.resolve(eventId);
    }
  }

  const dialog = asking ? (
    <Dialog title="Which event?" small onClose={() => void answer(undefined)}>
      <p className="note">
        No current event on this device. Every contact and lead you capture is tagged with it.
      </p>
      <div className="evlist">
        {asking.shows.map((e) => (
          <button key={e.id} type="button" onClick={() => void answer(e.id)}>
            <span>
              <b className="evlist-name">{eventName(e)}</b>
              <br />
              <small className="note">{eventPlace(e)}</small>
            </span>
          </button>
        ))}
        <button type="button" onClick={() => void answer(null)}>
          No event (office, visit, call)
        </button>
      </div>
    </Dialog>
  ) : null;

  return { eventForCapture, dialog };
}
